import ReactGA from 'react-ga4';

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

/**
 * Initialize Google Analytics 4
 * Only runs in production
 */
export const initializeAnalytics = () => {
  if (GA_MEASUREMENT_ID && import.meta.env.PROD) {
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      gaOptions: {
        anonymizeIp: true, // GDPR compliance
      },
    });
    console.log('[Analytics] Initialized GA4');
  } else {
    console.log('[Analytics] GA4 disabled (dev mode or missing ID)');
  }
};

/**
 * Track page views
 * @param {string} path - Page path (e.g., '/leaderboard')
 */
export const trackPageView = (path) => {
  if (import.meta.env.PROD) {
    ReactGA.send({ hitType: 'pageview', page: path });
  }
};

/**
 * Generic event tracking
 * @param {string} category - Event category (e.g., 'User', 'Game', 'Table')
 * @param {string} action - Event action (e.g., 'Sign Up', 'Submit')
 * @param {string|null} label - Optional label for additional context
 * @param {number|null} value - Optional numeric value
 */
export const trackEvent = (category, action, label = null, value = null) => {
  if (import.meta.env.PROD) {
    const eventData = {
      category,
      action,
    };

    // Only include optional parameters if they have values
    if (label !== null) {
      eventData.label = label;
    }
    if (typeof value === 'number') {
      eventData.value = value;
    }

    ReactGA.event(eventData);
  }

  // Always log in development for debugging
  if (!import.meta.env.PROD) {
    console.log('[Analytics Event]', { category, action, label, value });
  }
};

/**
 * Convenience functions for common events
 * Organized by feature area
 */
export const analytics = {
  // === Authentication ===
  signUp: (method) => trackEvent('User', 'Sign Up', method),
  login: (method) => trackEvent('User', 'Login', method),
  logout: () => trackEvent('User', 'Logout'),
  emailVerified: () => trackEvent('User', 'Email Verified'),
  smsVerified: () => trackEvent('User', 'SMS Verified'),

  // === Games ===
  createGame: (type, tableId) => trackEvent('Game', 'Create', type, tableId),
  submitGame: (gameId) => trackEvent('Game', 'Submit', gameId),
  cancelGame: (gameId, reason) => trackEvent('Game', 'Cancel', reason, gameId),
  eloChange: (amount, direction) => trackEvent('Game', 'ELO Change', direction, amount),

  // === Social ===
  sendFriendRequest: (recipientId) => trackEvent('Social', 'Friend Request Sent', recipientId),
  acceptFriendRequest: (senderId) => trackEvent('Social', 'Friend Request Accepted', senderId),
  declineFriendRequest: (senderId) => trackEvent('Social', 'Friend Request Declined', senderId),
  viewProfile: (userId, source) => trackEvent('Social', 'Profile Viewed', source, userId),

  // === Tables ===
  addTable: (tableId, tableName) => trackEvent('Table', 'Add', tableName, tableId),
  viewTable: (tableId, source) => trackEvent('Table', 'View', source, tableId),
  searchTables: (query, resultsCount) => trackEvent('Table', 'Search', query, resultsCount),
  interactWithMap: (action) => trackEvent('Table', 'Map Interaction', action),

  // === Navigation ===
  viewLeaderboard: (filter) => trackEvent('Navigation', 'Leaderboard', filter),
  viewGameHistory: (userId) => trackEvent('Navigation', 'Game History', userId),
  viewNotifications: (unreadCount) => trackEvent('Navigation', 'Notifications', null, unreadCount),
  clickNotification: (type) => trackEvent('Navigation', 'Notification Clicked', type),

  // === Engagement ===
  submitFeedback: (type) => trackEvent('Engagement', 'Feedback Submitted', type),
  viewStatusPage: () => trackEvent('Engagement', 'Status Page Viewed'),

  // === Admin ===
  accessAdminPanel: () => trackEvent('Admin', 'Panel Accessed'),
  changeUserRole: (userId, newRole) => trackEvent('Admin', 'Role Changed', newRole, userId),

  // === Errors ===
  apiError: (endpoint, statusCode) => trackEvent('Error', 'API Error', endpoint, statusCode),
  authError: (type) => trackEvent('Error', 'Auth Error', type),
  formError: (formName, fieldName) => trackEvent('Error', 'Form Validation', `${formName}:${fieldName}`),
};

export default analytics;
